import { useSyncExternalStore } from 'react'
import { LineData, Time } from 'lightweight-charts'
import {
    multiStrikeSnapshotChangedKeys,
    MultiStrikeSeriesSnapshot,
} from './multiStrikeChartSnapshotEquality'

type Listener = () => void

const EMPTY_SNAPSHOT: MultiStrikeSeriesSnapshot = {}

const liveSnapshots: Record<string, MultiStrikeSeriesSnapshot> = {}
const crosshairSnapshots: Record<string, MultiStrikeSeriesSnapshot | null | undefined> = {}
const crosshairRevisionByViewKey: Record<string, number> = {}
const seriesListeners: Record<string, Set<Listener>> = {}
const crosshairListeners: Record<string, Set<Listener>> = {}

function seriesListenerKey(viewKey: string, seriesKey: string) {
    return `${viewKey}:${seriesKey}`
}

function notifySeriesListeners(viewKey: string, seriesKeys: string[]) {
    seriesKeys.forEach((seriesKey) => {
        seriesListeners[seriesListenerKey(viewKey, seriesKey)]?.forEach((listener) => listener())
    })
}

function notifyCrosshairListeners(viewKey: string) {
    crosshairListeners[viewKey]?.forEach((listener) => listener())
}

export function setMultiStrikeChartLiveSnapshot(viewKey: string, snapshot: MultiStrikeSeriesSnapshot) {
    const prev = liveSnapshots[viewKey]
    const changedKeys = multiStrikeSnapshotChangedKeys(prev, snapshot)
    if (changedKeys.length === 0) return
    liveSnapshots[viewKey] = snapshot
    notifySeriesListeners(viewKey, changedKeys)
}

export function setMultiStrikeChartCrosshairSnapshot(
    viewKey: string,
    snapshot: MultiStrikeSeriesSnapshot | null
) {
    crosshairSnapshots[viewKey] = snapshot
}

export function isMultiStrikeCrosshairSnapshotActive(viewKey: string): boolean {
    return crosshairSnapshots[viewKey] != null
}

export function bumpMultiStrikeCrosshairRevision(viewKey: string) {
    crosshairRevisionByViewKey[viewKey] = (crosshairRevisionByViewKey[viewKey] ?? 0) + 1
    notifyCrosshairListeners(viewKey)
}

export function getMultiStrikeLiveSnapshot(viewKey: string): MultiStrikeSeriesSnapshot {
    return liveSnapshots[viewKey] ?? EMPTY_SNAPSHOT
}

export function getEffectiveMultiStrikeSeriesPoint(
    viewKey: string,
    seriesKey: string
): LineData<Time> | undefined {
    const livePoint = liveSnapshots[viewKey]?.[seriesKey]
    const crosshair = crosshairSnapshots[viewKey]
    if (crosshair) {
        const crosshairPoint = crosshair[seriesKey]
        if (crosshairPoint?.value !== undefined && Number.isFinite(crosshairPoint.value)) {
            return crosshairPoint
        }
    }
    return livePoint
}

export function subscribeMultiStrikeSeriesSnapshot(
    viewKey: string,
    seriesKey: string,
    listener: Listener
) {
    const key = seriesListenerKey(viewKey, seriesKey)
    if (!seriesListeners[key]) {
        seriesListeners[key] = new Set()
    }
    seriesListeners[key].add(listener)
    return () => {
        seriesListeners[key]?.delete(listener)
    }
}

export function subscribeMultiStrikeCrosshairRevision(viewKey: string, listener: Listener) {
    if (!crosshairListeners[viewKey]) {
        crosshairListeners[viewKey] = new Set()
    }
    crosshairListeners[viewKey].add(listener)
    return () => {
        crosshairListeners[viewKey]?.delete(listener)
    }
}

/** Live ticks + crosshair moves for a series metadata value strip. */
export function subscribeMultiStrikeLiveData(
    viewKey: string,
    seriesKeys: string[],
    listener: Listener
) {
    const uniqueKeys = Array.from(new Set(seriesKeys.filter(Boolean)))
    const unsubs = uniqueKeys.map((key) => subscribeMultiStrikeSeriesSnapshot(viewKey, key, listener))
    unsubs.push(subscribeMultiStrikeCrosshairRevision(viewKey, listener))
    return () => {
        unsubs.forEach((unsub) => unsub())
    }
}

export function clearMultiStrikeChartSnapshot(viewKey: string) {
    delete liveSnapshots[viewKey]
    delete crosshairSnapshots[viewKey]
    delete crosshairRevisionByViewKey[viewKey]
}

/** @deprecated Use granular snapshot subscriptions instead. */
export function setMultiStrikeChartSnapshot(
    viewKey: string,
    snapshot: Map<string, LineData<Time>>,
    _hoveredTime: number
) {
    const record: MultiStrikeSeriesSnapshot = {}
    snapshot.forEach((value, key) => {
        record[String(key)] = value
    })
    setMultiStrikeChartLiveSnapshot(viewKey, record)
}
